import React, { useRef } from "react";
import Resources from "../../../types/Resources";
import { Date_isSameDate, Date_toDateString } from "../../../common/dateUtils";
import useDateInput from "../../../hooks/useDateInput";
import { PermitFormState } from "../../../types/permits";
import { Transformation } from "../../../types/functions";
import TextInput from "../../Common/TextInput";

type RemindMeProps<TPermitForm extends PermitFormState> = {
    resources: Resources;
    formState: TPermitForm;
    onChange: (transformation: Transformation<TPermitForm>) => void;
    readOnly: boolean;
};

function RemindMe<TPermitForm extends PermitFormState>({
    resources,
    onChange,
    formState,
    readOnly
}: RemindMeProps<TPermitForm>) {
    const dateRef = useRef<HTMLInputElement>(null);

    function changeHandler(key: keyof TPermitForm, value: any) {
        if (!formState[key]) {
            console.error("Invalid key");
            return;
        }

        onChange((prevValue) => ({ ...prevValue, [key]: { ...prevValue[key], value: value, errorMessage: "" } }));
    }

    const dateHandler = useDateInput({
        date: formState.remindMeDate.value,
        ref: dateRef,
        comparer: Date_isSameDate,
        toStringValue: Date_toDateString,
        onDateChange: (updatedDate) => changeHandler("remindMeDate", updatedDate)
    });

    return (
        <div className="row mt-2">
            <div className="form-group col-md-3">
                <label className="fw-bold">{resources.get("RemindMe")}</label>
                <div>
                    <input
                        className={`me-1 form-check-input ${readOnly ? "read-only-check" : ""}`}
                        type="radio"
                        value="true"
                        onChange={(e) => (!readOnly ? changeHandler("remindMe", true) : false)}
                        checked={formState.remindMe.value}
                        id="remind-me__yes"
                    />
                    <label htmlFor="remind-me__yes" className={`me-2 ${readOnly ? "pe-none" : ""}`}>
                        {resources.get("Yes")}
                    </label>
                    <input
                        className={`me-1 form-check-input ${readOnly ? "read-only-check" : ""}`}
                        type="radio"
                        value="false"
                        onChange={(e) => (!readOnly ? changeHandler("remindMe", false) : false)}
                        checked={!formState.remindMe.value}
                        id="remind-me__no"
                    />
                    <label htmlFor="remind-me__no" className={readOnly ? "pe-none" : ""}>
                        {resources.get("No")}
                    </label>
                </div>
            </div>
            <TextInput
                inputId="expiration-reminder-email"
                type="email"
                value={formState.expirationReminderEmail.value ?? ""}
                readOnly={readOnly}
                labelText={resources.get("ExpirationReminderEmail")}
                onChange={(s) => changeHandler("expirationReminderEmail", s)}
                containerClassName="form-group col-md-3"
                errorMessage={formState.expirationReminderEmail.errorMessage}
            />
            <div className="form-group col-md-3">
                <label className="form-label fw-bold" htmlFor="remind-me-date">
                    {resources.get("RemindMeDate")}
                </label>
                <input
                    id="remind-me-date"
                    className={`form-control ${readOnly ? "read-only-input" : ""}`}
                    type="date"
                    defaultValue={Date_toDateString(formState.remindMeDate.value)}
                    onBlur={dateHandler.blurHandler}
                    aria-errormessage="remind-me-date-error"
                    ref={dateRef}
                    readOnly={readOnly}
                />
                <span id="remind-me-date-error" className="text-danger">
                    {formState.remindMeDate.errorMessage}
                </span>
            </div>
        </div>
    );
}

export default RemindMe;
